import { FormSectionHandler } from '../entity.handler';
import { DashboardTemplateResponse, DashboardTemplateUpdateRequest, DashboardWidget } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { TextField, EntityUtils, ToggleField, ComboField } from '../utils';
import { ICONS } from '../icons';
import { DashboardsService } from 'app/services';
import { EditDashboardModalComponent } from '../../dashboard/dashboard-modal.component';

interface EditorValues {
    dashboardId?: string;
    name?: string;
    description?: string;
    public?: boolean;
}

export class DashboardTemplatesDetailsSectionHandler implements FormSectionHandler<DashboardTemplateResponse, EditorValues> {

    type = 'form';
    icon = ICONS.dashboardtemplate;
    bulk = true;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    actions = [{ id: 'open', name: this.admin.translate('EDITORS.DASHBOARD_TEMPLATES.COLUMNS.EDIT_TEMPLATE'), showAlways: true }];

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        public dashbaords: DashboardsService,
        private creating?: boolean
    ) {
    }

    private properties = ['dashboardId', 'name', 'description', 'public'];

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.DASHBOARD_TEMPLATES.DETAILS.${key}`, values);
    }

    applyUpdate(entity: DashboardTemplateResponse, payload: DashboardTemplateUpdateRequest): Promise<DashboardTemplateResponse> {
        return this.admin.applyUpdate(api => api.entities.updateDashboardTemplate(entity.id, payload), { _type: 'dashboardtemplate' });
    }

    mapEntityToEditor(entity: DashboardTemplateResponse): Promise<EditorValues> {
        return Promise.resolve(EntityUtils.mergeFields(this.properties, {
            ...entity,
            dashboardId: entity.id,
        }, {}));
    }

    mapEditorToEntity(entity: DashboardTemplateUpdateRequest, values: EditorValues): Promise<DashboardTemplateUpdateRequest> {
        return Promise.resolve(EntityUtils.mergeFields(this.properties, values, entity));
    }

    async getFields(entity: DashboardTemplateResponse): Promise<FormBuilderField[]> {
        const dashboards = await this.dashbaords.getDashboards(this.ownerId);
        const fields = [
            this.creating ? ComboField('dashboardId', this.translate('FIELDS.DASHBOARD'), true, (dashboards || []).map(dashboard => ({
                key: dashboard.id,
                value: dashboard.name,
            }))) : null,
            TextField('name', this.translate('FIELDS.NAME'), true, 1, 255),
            TextField('description', this.translate('FIELDS.DESCRIPTION'), true, 1, 1024),
            ToggleField('public', this.translate('FIELDS.PUBLIC'), true),
        ].filter(x => x);
        return EntityUtils.prepareBulk(!entity, ['description'], fields);
    }

    async actionTriggered(entity: DashboardTemplateResponse): Promise<void> {
        const dashboard: DashboardTemplateResponse = await this.admin.getCached(`getDashboardTemplate/${entity.id}`, api => api.entities.getDashboardTemplate(entity.id), 10, true);

        this.admin.modal.open(EditDashboardModalComponent, {
            data: {
                widgets: dashboard.widgets,
                id: dashboard.id,
                title: dashboard.name,
                useSampleData: true,
                isDashboardTemplate: true,
            },
            actions: {
                close: () => {
                    this.admin.modal.close();
                },
                save: async (widgets: { [id: string]: DashboardWidget }) => {
                    try {
                        await this.applyUpdate(dashboard, { widgets });
                    } finally { // only close modal after request
                        this.admin.modal.close();
                    }
                },
            },
        });
    }

}
